// Brand-specific sample card payloads for the "Load sample" demo button on
// the Reservation Detail page. Each brand has a distinct, realistic-looking
// format so the demo flow tells a believable story:
//   • Home Depot / Lowe's / Best Buy / Walmart → 16-digit numeric + 4-digit PIN + $face
//   • Target → 19-digit + 4-digit access code
//   • Starbucks → 16-digit + 8-digit security code, smaller face values
//   • Steam → activation-code grid (AAAA-BBBB-CCCC) + no PIN, fixed $20/$50/$100
//   • PlayStation → 12-character activation code, fixed denominations
//
// Numbers are obviously fake (start with 6006... or repeat digits) to avoid
// any chance of resembling real card BINs. Face values are chosen so the
// total respects typical perMemberCap ($2,000–$5,000) and the deal's minFace.

const cardLine = (num, pin, face) => `${num}   PIN ${pin}   $${face}`;

// Generate three brand-tailored sample lines. Returns a textarea-ready string.
export function sampleCardLines(brandId, brandName, minFace = 100, maxFace = 500) {
  // Choose face values inside the brand's allowed range.
  const f1 = Math.max(minFace, Math.min(maxFace, 200));
  const f2 = Math.max(minFace, Math.min(maxFace, 250));
  const f3 = Math.max(minFace, Math.min(maxFace, 100));

  switch (brandId) {
    case "brand_home_depot":
      return [
        cardLine("6006491234567812", "5582", f1),
        cardLine("6006491234567828", "1029", f2),
        cardLine("6006491234567834", "4421", f3),
      ].join("\n");
    case "brand_lowes":
      return [
        cardLine("6006498765432101", "8841", f1),
        cardLine("6006498765432117", "2266", f2),
        cardLine("6006498765432129", "7733", f3),
      ].join("\n");
    case "brand_best_buy":
      return [
        cardLine("6006498800012345", "9911", f1),
        cardLine("6006498800023456", "4422", f2),
        cardLine("6006498800034567", "8855", f3),
      ].join("\n");
    case "brand_walmart":
      return [
        cardLine("6037834500001234", "1245", f1),
        cardLine("6037834500005678", "6677", f2),
        cardLine("6037834500009012", "3399", f3),
      ].join("\n");
    case "brand_target":
      return [
        cardLine("6065100000123456789", "2244", f1),
        cardLine("6065100000234567890", "8866", f2),
        cardLine("6065100000345678901", "5511", f3),
      ].join("\n");
    case "brand_starbucks":
      return [
        cardLine("6085610000112233", "20349715", Math.min(50, maxFace)),
        cardLine("6085610000223344", "47118822", Math.min(25, maxFace)),
        cardLine("6085610000334455", "99812044", Math.min(100, maxFace)),
      ].join("\n");
    case "brand_steam": {
      // Steam wallet codes are alphanumeric activation grids (no PIN).
      const codes = ["3X7K9-MN4PQ-RB82L", "Z9HJ4-W2VKD-P5C7T", "Y4N8M-X3RQB-K7HV2"];
      const faces = [50, 100, 20].map(v => Math.min(Math.max(v, minFace), maxFace));
      return codes.map((c, i) => `${c}   $${faces[i]}`).join("\n");
    }
    case "brand_playstation": {
      const codes = ["PSN4-XK9M-2H7Q", "PSN8-RT3W-LN5P", "PSN2-QZ7V-MJ4D"];
      const faces = [50, 25, 100].map(v => Math.min(Math.max(v, minFace), maxFace));
      return codes.map((c, i) => `${c}   $${faces[i]}`).join("\n");
    }
    default:
      // Generic 16-digit fallback for any newly added brand.
      return [
        cardLine("4000123412341234", "1111", f1),
        cardLine("4000123412345678", "2222", f2),
        cardLine("4000123412349012", "3333", f3),
      ].join("\n");
  }
}
